import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './store';
import Navigation from './components/NavigationSideBar';
import LoaderThreeDots from './components/LoaderThreeDots';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import AuthProvider from './services/authProvider';
import userManager, { loadUserFromStorage } from './services/userService';
import SigninOidc from './pages/Authentication/signin-oidc';
import SignoutOidc from './pages/Authentication/signout-oidc';
import { ConfirmContextProvider } from './hooks/useConfirm/Provider';
import PromptTextProvider from './hooks/usePromptText/Provider';
// import CloseoutAlert from './components/CloseoutAlert';
import { SelectContextProvider } from './hooks/useSelect/Provider';
import GenerateFitment from './pages/GenerateFitment';
import ClassLineDescription from './pages/ClassLineDescription';
import ListingData from './pages/ListingData';
// import CloseoutV2 from './pages/CloseoutV2';
import InventoryMovementView from './pages/InventoryMovements';

const ServiceMonitorContainer = lazy(() => import('./pages/ServiceMonitor'));
const ConveyorMonitor = lazy(() => import('./pages/ConveyorMonitor'));
const PackingMonitor = lazy(() => import('./pages/PackingMonitor'));
const Training = lazy(() => import('./pages/Training'));
const EdiManagerContainer = lazy(() => import('./pages/EdiManager'));
const ManageUsers = lazy(() => import('./pages/Security/ManageUsers'));
const ManageAccess = lazy(() => import('./pages/Security/ManageAccess'));
const Closeout = lazy(() => import('./pages/Closeout'));
const ManageShipper = lazy(() => import('./pages/ManageShipper'));
const OrdersSummary = lazy(() => import('./pages/OrdersSummary'));
const FitmentApp = lazy(() => import('./pages/FitmentApp'));
const OrdersSummaryBySKU = lazy(() => import('./pages/OrdersSummaryBySKU'));
// const TrackingShipments = lazy(() => import('./pages/TrackingShipments'));
const WillsInbound = lazy(() => import('./pages/Wills/WillsInbound'));
const WillsOutbound = lazy(() => import('./pages/Wills/WillsOutbound'));
const WillsSettings = lazy(() => import('./pages/Wills/WillsSettings'));
const WillsDiscrepancies = lazy(() => import('./pages/Wills/WillsDiscrepancies'));
const WillsTruckInbound = lazy(() => import('./pages/Wills/WillsTruckInbound'));
const CrossReferenceLookup = lazy(() => import('./pages/CrossReferenceLookup'));
const ProductLookup = lazy(() => import('./pages/PackagePartLookup'));

function App() {
  React.useEffect(() => {
    // fetch current user from cookies
    loadUserFromStorage(store);
  }, []);

  return (
    <Provider store={store}>
      <ConfirmContextProvider>
        <PromptTextProvider>
          <SelectContextProvider>
            <AuthProvider userManager={userManager} store={store}>
              <BrowserRouter>
                <Navigation>
                  <Suspense fallback={<LoaderThreeDots title="Loading..." />}>
                    <Routes>
                      <Route path="*" element={<NotFound />} />
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/" element={<ProtectedRoute />}>
                        <Route path="/service_monitor" element={<ServiceMonitorContainer />} />
                        <Route path="/conveyor_monitor" element={<ConveyorMonitor />} />
                        <Route path="/packing_monitor" element={<PackingMonitor />} />
                        <Route path="/closeout" element={<Closeout />} />
                        {/* <Route path="/closeout-v2" element={<CloseoutV2 />} /> */}
                        <Route path="/packing_monitor/edit_packages" element={<PackingMonitor />} />
                        <Route path="/training" element={<Training />} />
                        <Route path="/edi" element={<EdiManagerContainer />} />
                        <Route path="/manage_users" element={<ManageUsers />} />
                        <Route path="/manage_access" element={<ManageAccess />} />
                        <Route path="/shipping_lanes" element={<ManageShipper />} />
                        <Route path="/order_status" element={<OrdersSummary />} />
                        <Route path="/ninety_day_sales_by_sku" element={<OrdersSummaryBySKU />} />
                        <Route path="/fitment" element={<FitmentApp />} />
                        {/* <Route path="/tracking_shipments" element={<TrackingShipments />} /> */}
                        <Route path="/wills_inbound" element={<WillsInbound />} />
                        <Route path="/wills_outbound" element={<WillsOutbound />} />
                        <Route path="/wills_settings" element={<WillsSettings />} />
                        <Route path="/wills_discrepancies" element={<WillsDiscrepancies />} />
                        <Route path="/wills_truck_inbound" element={<WillsTruckInbound />} />
                        <Route path="/cross_ref_search" element={<CrossReferenceLookup />} />
                        <Route path="/product_search" element={<ProductLookup />} />
                        <Route path="/generate_fiment" element={<GenerateFitment />} />
                        <Route path="/cld" element={<ClassLineDescription />} />
                        <Route path="/feedonomics_data" element={<ListingData />} />
                        <Route path="/inv" element={<InventoryMovementView />} />
                      </Route>
                      <Route path="/signout-oidc" element={<SignoutOidc />} />
                      <Route path="/signin-oidc" element={<SigninOidc />} />
                    </Routes>
                    {/* <CloseoutAlert /> */}
                  </Suspense>
                </Navigation>
              </BrowserRouter>
            </AuthProvider>
          </SelectContextProvider>
        </PromptTextProvider>
      </ConfirmContextProvider>
    </Provider>
  );
}

export default App;
